import { createContext, type SetStateAction, useContext } from "react";
import {
  type LookalikeAudienceStatistics,
  type MediaDataRoomJobHookResultWithoutCacheSetter,
} from "features/mediaDataRoom/hooks";
import { MediaDataRoomVersion } from "features/mediaDataRoom/models";
import { type LookalikeAudienceSeedOption } from "../models";

export interface LookalikeAdvertiserAudienceGeneratorContextValue {
  isAdvertiserAudienceQualityDrawerOpen: boolean;
  openAdvertiserAudienceQualityDrawer: () => void;
  closeAdvertiserAudienceQualityDrawer: () => void;
  reach: number;
  setReach: (reach: SetStateAction<number>) => void;
  excludeSeedAudience: boolean;
  setExcludeSeedAudience: (
    excludeSeedAudience: SetStateAction<boolean>
  ) => void;
  audienceName: string;
  setAudienceName: (name: SetStateAction<string>) => void;
  selectedSeedOption: LookalikeAudienceSeedOption | null;
  setSelectedSeedOption: (
    audience: SetStateAction<LookalikeAudienceSeedOption | null>
  ) => void;
  generationStep: "pending" | "completed" | null;
  generateAudience: () => void;
  showExcludeSeedAudienceCheckbox: boolean;
  disableExcludeSeedAudienceCheckbox: boolean;
  seedOptions: MediaDataRoomJobHookResultWithoutCacheSetter<
    LookalikeAudienceSeedOption[]
  >;
  selectedAudienceStatistics: LookalikeAudienceStatistics | null;
  version: MediaDataRoomVersion;
}

export const LookalikeAdvertiserAudienceGeneratorContext =
  createContext<LookalikeAdvertiserAudienceGeneratorContextValue>({
    audienceName: "",
    closeAdvertiserAudienceQualityDrawer: () => {},
    disableExcludeSeedAudienceCheckbox: false,
    excludeSeedAudience: false,
    generateAudience: () => {},
    generationStep: null,
    isAdvertiserAudienceQualityDrawerOpen: false,
    openAdvertiserAudienceQualityDrawer: () => {},
    reach: 1,
    seedOptions: {
      computeResults: [],
      error: undefined,
      loading: false,
      retry: async () => {},
      status: "COMPLETED",
    },
    selectedAudienceStatistics: null,
    selectedSeedOption: null,
    setAudienceName: () => {},
    setExcludeSeedAudience: () => {},
    setReach: () => {},
    setSelectedSeedOption: () => {},
    showExcludeSeedAudienceCheckbox: false,
    version: MediaDataRoomVersion.ab,
  });

export const LookalikeAdvertiserAudienceGeneratorProvider =
  LookalikeAdvertiserAudienceGeneratorContext.Provider;

export const useLookalikeAdvertiserAudienceGenerator = () =>
  useContext(LookalikeAdvertiserAudienceGeneratorContext);
